// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { isEmpty, pickBy, sortBy } from 'lodash';
import { API_ENDPOINTS } from '../const/ApiEndpoints';
import { Constants } from '../const';
import { selectAuth } from '../state/slices/appSlice';
import { safeJoin, buildTrueMatchFlags } from '../utils/Utils';
import useDashBoard from './useDashboard';

const PAYLOAD_DATE_FORMAT = 'YYYY-MM-DD';
const { BATCH_FILE_PAGE_SIZE } = Constants.CommonConst;

/**
 * @function useDownloads
 * @description a custom hook which handles APIs related to download status and report screen
 * @returns {object} object
 */
export default function useDownloads() {
  const { user } = useSelector(selectAuth);
  const { defaultRatings } = useSelector((state) => state.common);
  const [downloads, setDownloads] = useState([]);
  const [downloadStatus, setDownloadStatus] = useState([]);
  const [excelId, setExcelId] = useState([]);
  const [statusUrl, setStatusUrl] = useState({});
  const [reportUrls, setReportUrls] = useState({});
  const [excelReportId, setExcelReportId] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [teamList, setTeamList] = useState([]);
  const [filters, setFilters] = useState(null);
  const [fieldsList, setFieldsList] = useState([]);
  const [tableColumns, setTableColumns] = useState({});
  const [newTableColumns, setNewTableColumns] = useState({});
  const [channel, setChannel] = useState({});
  const [transactionType, setTransactionType] = useState({});
  const [productType, setProductType] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const { userTypes, analystType } = useDashBoard();

  function buildModifiedFilters(tableFilters) {
    if (isEmpty(tableFilters)) return {};
    const LIST_TYPE_MAP = {
      Sanctions: 'sanction',
      PEP: 'PEP',
      AMS: 'AMS',
      Corporate: 'corporate',
      Legal: 'legal',
      MNRL: 'mnrl',
    };

    const countries = sortBy(
      Constants?.CommonConst?.countries || [],
      'label'
    ).map(({ label }) => ({ label, value: label }));

    const processors = {
      alerted: (value) => {
        const alertValue =
          Array.isArray(value) && value.length === 1 ? value[0] : value;
        if (alertValue === 'Alert') return 'true';
        if (alertValue === 'Non-Alert') return 'false';
        return undefined;
      },

      status: (value) => {
        const statusArray = Array.isArray(value) ? value : [];
        return statusArray
          .map((item) => (item === 'Pending' ? 'q' : item?.[0]?.toLowerCase()))
          .join('');
      },

      risk_type: (value) => {
        if (!value?.length) return { risk_rating: false, risk_type: 'none' };
        if (value.length === defaultRatings?.length)
          return { risk_rating: true, risk_type: 'all' };

        const riskTypeArray = Array.isArray(value) ? value : [];
        return {
          risk_rating: true,
          risk_type: riskTypeArray.join(',').toLowerCase(),
        };
      },

      listType: (value) => {
        if (!Array.isArray(value)) return {};

        const result = {};
        Object.entries(LIST_TYPE_MAP).forEach(([listKey, apiKey]) => {
          result[apiKey] = value.includes(listKey);
        });
        return result;
      },
      true_match: (value) => {
        if (!Array.isArray(value)) {
          return {
            sanction_true_match: 'none',
            pep_true_match: 'none',
            ams_true_match: 'none',
          };
        }

        return buildTrueMatchFlags(value);
      },

      dates: (value) => {
        if (value?.start && value?.end) {
          return {
            start_date: moment(value.start).format(PAYLOAD_DATE_FORMAT),
            end_date: moment(value.end).format(PAYLOAD_DATE_FORMAT),
          };
        }
        return {
          start_date: null,
          end_date: null,
        };
      },

      due_date: (value) => {
        if (value?.start && value?.end) {
          return {
            start_due_date: moment(value.start).format(PAYLOAD_DATE_FORMAT),
            end_due_date: moment(value.end).format(PAYLOAD_DATE_FORMAT),
          };
        }
        return {
          start_due_date: null,
          end_due_date: null,
        };
      },

      search_type: (value) => (value?.length ? safeJoin(value, ',') : ''),

      batch_id: (value, filtersObj) => {
        if (filtersObj?.select_all_batches) {
          return undefined;
        }

        return value?.length
          ? safeJoin(
              value.map((item) => item.value),
              ','
            )
          : undefined;
      },

      country: (value) => ({
        unselected_country: countries
          .filter((item) => !value?.includes(item.value))
          .map((item) => item.value)
          .join(','),
        country: value?.length ? safeJoin(value, ',') : '',
      }),

      default: (value) => (Array.isArray(value) ? safeJoin(value, ',') : value),
    };

    const result = {};
    Object.entries(tableFilters).forEach(([key, value]) => {
      const processor = processors[key] || processors.default;
      const processed = processor(value, tableFilters);

      if (typeof processed === 'object' && processed !== null) {
        Object.assign(result, processed);
      } else if (processed !== undefined) {
        result[key] = processed;
      }
    });

    return pickBy(result, (value, key) => {
      if (['team', 'baseStr', 'analyst_name'].includes(key)) {
        return value !== '';
      }
      return value !== undefined && value !== null;
    });
  }

  /**
   * @function fetchDownloadReportLink
   * @description A function to fetch download reports link data
   * @returns {Promise} returns an axios promise
   */
  const fetchDownloadReportLink = () => {
    const params = excelReportId;
    return axios
      .get(`${API_ENDPOINTS.DOWNLOAD_REPORT}${params}`, { loader: false })
      .then(
        (response) => {
          if (
            response?.status === 200 &&
            response.data?.Msg !== 'Processing already started!'
          ) {
            if (response.data?.Msg) {
              enqueueSnackbar(response.data?.Msg, {
                variant: 'success',
              });
            } else {
              setReportUrls(response.data);
              enqueueSnackbar('Files downloaded successfully.', {
                variant: 'success',
              });
            }
          }
          return response;
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
          throw e;
        }
      );
  };

  /**
   * @function getDownloadData
   * @description function for fetching download data for BaseTable
   * @returns {Promise} promise that resolves with data and totalRowCount
   */
  const getDownloadData = useCallback(
    ({ page: tablePage = 0, pageSize = 5, filters: tableFilters }) => {
      const modifiedFilters = buildModifiedFilters(tableFilters);
      const { module, ...rest } = modifiedFilters;
      // Add pagination
      const params = {
        ...rest,
        transaction_screening:
          module === 'transaction_screen' ? 'true' : 'false',
        pagesize: pageSize,
        page: tablePage + 1, // BaseTable uses 0-based indexing, but API might use 1-based
      };

      return axios.post(API_ENDPOINTS.DOWNLOAD_REPORT, params).then((res) => {
        if (res.data) {
          setExcelReportId(res.data?.id);
          setDownloads(res.data.data);
        }
        return {
          data: res?.data?.data || [],
          totalRowCount: res?.data?.total_count || res?.data?.data?.length || 0,
          hasNext: res?.data?.has_more_rows || false,
        };
      });
    },
    [defaultRatings]
  );

  const fetchBatchFileOptions = useCallback(
    async ({ search = '', page = 1, filterData }) => {
      const response = await axios.get(API_ENDPOINTS.DASHBOARD_BATCH_INFO, {
        params: {
          page,
          pagesize: BATCH_FILE_PAGE_SIZE,
          minimal: true,
          screen_type: filterData?.screeningType,
          trxn_dashboard: filterData?.module === 'transaction_screen',
          ...(search ? { file_name: search } : {}),
        },
        loader: false,
      });

      const rows = [
        ...(Array.isArray(response?.data) ? response.data : []),
      ].sort(
        (first, second) => Number(second?.id || 0) - Number(first?.id || 0)
      );
      const totalCount = Number(response?.headers?.['x-total-count'] || 0);
      const hasMore = totalCount > page * BATCH_FILE_PAGE_SIZE;
      const result = {
        options: rows
          .filter((item) => item?.file_name && item?.batch_id)
          .map((item) => ({
            id: item.id,
            label: item.file_name,
            value: item.batch_id,
            batch_id: item.batch_id,
            file_name: item.file_name,
            title: `${item.file_name} (${item.batch_id})`,
          })),
        hasMore,
      };

      return result;
    },
    []
  );

  /**
   * @function customizeFilterAndColumns
   * @description A function to customize filters and table columns
   * @returns {Promise} returns an axios promise
   */
  const customizeFilterAndColumns = (data, silent = false) => {
    const params = { ...data };
    setIsLoading(true);
    // Clear columns immediately to prevent BaseTable from rendering with stale
    // columns from the previous module while the new API call is in flight
    if (!silent) {
      setTableColumns({});
    }
    return axios
      .get(API_ENDPOINTS.CUSTOMIZE_FILTERS_AND_COLUMNS, {
        params,
      })
      .then(
        (response) => {
          if (response?.status === 200) {
            setIsLoading(false);
            setFilters(response?.data?.filter_criteria);

            // Convert object to object with code_name as keys (like DownloadsTable expects)
            // selected_column is already an object, so we extract its values and combine with other_fields children
            const selectedColumnValues = Object.values(
              response?.data?.selected_column || {}
            );
            const otherFieldsChildren =
              response?.data?.selected_column?.other_fields?.children || [];

            const columnsArray = [
              ...selectedColumnValues.filter(
                (item) => item?.code_name !== 'other_fields'
              ), // Exclude other_fields object itself
              ...otherFieldsChildren,
            ];

            const columnsObject = columnsArray.reduce((acc, column) => {
              if (column?.code_name) {
                acc[column.code_name] = column;
              }
              return acc;
            }, {});

            setTableColumns(columnsObject);
            setNewTableColumns(response?.data?.selected_column);
          }
          return response;
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
          throw e;
        }
      );
  };
  /**
   * @function addCustomizeFilterAndColumns
   * @description A function to add customize filters and table columns
   * @returns {Promise} returns an axios promise
   */
  const addCustomizeFilterAndColumns = (data, silent = false) => {
    const body = { ...data };
    return axios.post(API_ENDPOINTS.CUSTOMIZE_FILTERS_AND_COLUMNS, body).then(
      (response) => {
        if (!silent) {
          enqueueSnackbar(response.data?.message, {
            variant: 'success',
          });
        }
        customizeFilterAndColumns(
          {
            selected_column: true,
            filters: true,
            feature_name: data?.get_feature_name || data?.feature_name,
          },
          silent
        );
        return response;
      },
      (e) => {
        if (!silent) {
          setIsLoading(false);
          setIsError(e);
        }
        throw e;
      }
    );
  };
  /**
   * @function getTransactionParameters
   * @description A function to get transaction parameters
   * @returns {Promise} returns an axios promise
   */
  const getTransactionParameters = (queryParams) => {
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.TRANSACTION_SCREENING_RULE_ENGINE, {
        params: queryParams,
      })
      .then(
        (response) => {
          setIsLoading(false);
          response?.data?.data?.forEach((item) => {
            if (item?.name === 'channel_type') {
              setChannel({ values: item?.values, mandatory: item?.mandatory });
            }
            if (item?.name === 'transaction_type') {
              setTransactionType({
                values: item?.values,
                mandatory: item?.mandatory,
              });
            }
            if (item?.name === 'transaction_product_type') {
              setProductType({
                values: item?.values,
                mandatory: item?.mandatory,
              });
            }
          });
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
          throw e;
        }
      );
  };
  /**
   * @function fetchDownloadStatus
   * @description A function to fetch download status data
   * @returns {Promise} returns an axios promise
   */
  const fetchDownloadStatus = (data) => {
    const params = { ...data };
    params.start_date = moment(data.dates.start_date).format(
      PAYLOAD_DATE_FORMAT
    );
    params.end_date = moment(data.dates.end_date).format(PAYLOAD_DATE_FORMAT);
    params.due_date = moment(data.due_date).format(PAYLOAD_DATE_FORMAT);
    delete params.dates;
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.DOWNLOAD_STATUS, {
        params,
      })
      .then(
        (response) => {
          setIsLoading(false);
          if (response.data) {
            setExcelId(response.data?.id);
            setDownloadStatus(response.data.data);
          }
          return response;
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
        }
      );
  };
  /**
   * @function fetchDownloadStatusLink
   * @description A function to fetch download status link data
   * @returns {Promise} returns an axios promise
   */
  const fetchDownloadStatusLink = () => {
    const params = excelId;
    setIsLoading(true);
    return axios.get(`${API_ENDPOINTS.DOWNLOAD_STATUS_EXCEL}${params}`).then(
      (response) => {
        if (response?.status === 200) {
          setIsLoading(false);
          if (response.data?.Msg) {
            enqueueSnackbar(response.data?.Msg, {
              variant: 'success',
            });
          } else {
            setStatusUrl(response.data);
          }
        }
        return response;
      },
      (e) => {
        setIsLoading(false);
        setIsError(e);
        throw e;
      }
    );
  };
  /**
   * @function fetchTeamData
   * @description A function to fetch team data
   * @returns {Promise} returns an axios promise
   */
  const fetchTeamData = (queryParams) => {
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.INITIATE_SCREEN_TEAM_DATA, {
        params: queryParams,
      })
      .then(
        (response) => {
          setIsLoading(false);
          setTeamList(response?.data);
        },
        (e) => {
          setIsLoading(false);
          setIsError(e);
        }
      );
  };

  /**
   * @function fetchFieldOptions
   * @description A function to fetch options for a specific field
   * @param {string} field - The field name to fetch options for
   * @returns {Promise} returns an axios promise
   */
  const fetchFieldOptions = (screeningType, field) => {
    return axios
      .get(
        `${API_ENDPOINTS.GET_OTHER_FIELDS_LIST}?screening_type=${screeningType}&field=${field}&force_refresh=true`
      )
      .then(
        (response) => {
          setFieldsList(response.data);
          return response;
        },
        (e) => {
          setIsError(e);
          throw e;
        }
      );
  };

  useEffect(() => {
    fetchTeamData();
  }, []);
  return {
    isError,
    isLoading,
    user,
    downloads,
    setDownloads,
    teamList,
    downloadStatus,
    fetchDownloadStatus,
    setDownloadStatus,
    fetchDownloadStatusLink,
    statusUrl,
    fetchDownloadReportLink,
    getDownloadData,
    reportUrls,
    excelReportId,
    excelId,
    setExcelId,
    setStatusUrl,
    setReportUrls,
    userTypes,
    analystType,
    fetchTeamData,
    filters,
    setFilters,
    tableColumns,
    customizeFilterAndColumns,
    addCustomizeFilterAndColumns,
    getTransactionParameters,
    channel,
    transactionType,
    productType,
    newTableColumns,
    fieldsList,
    fetchFieldOptions,
    fetchBatchFileOptions,
  };
}
